import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { setUser } from '../../redux/auth/authSlice';
import { User } from '../../types';
import { tokenManager } from '../../utils/tokenManager';
import { completeOnboarding } from '../../services/api/onboardingCompletionService';
// completeOnboarding（上）は onboardingCompletionService = 組織作成 API。
// こちらの userService.completeOnboarding() は user.onboarding_completed を
// 立てる別 API なので名前が衝突しないよう userService 経由で呼ぶ。
import userService from '../../services/api/userService';
import {
  SIGNUP_CARD_STEP_ENABLED,
  SIGNUP_ONBOARDING_ENABLED,
} from '../../config/features';
import Image from '../../components/common/Image';
import {
  TaskyAlert,
  TaskyAuthLayout,
  TaskyButton,
  TaskyFormGroup,
  TaskyInput,
} from '../../components/tasky';
import {
  PARTNER_REF_REGEX,
  PARTNER_REF_STORAGE_KEY,
  PARTNER_TRIAL_DAYS_STORAGE_KEY,
  parsePartnerTrialDays,
} from '../../constants/partnerRef';
import { INVITE_REF_REGEX, INVITE_REF_STORAGE_KEY } from '../../constants/inviteRef';

interface FieldErrors {
  company_name?: string;
  name?: string;
  invitation_code?: string;
}

const CompanyOnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const [companyName, setCompanyName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [showInvitationCode, setShowInvitationCode] = useState(false);
  // 招待URL(?partner_ref=CODE)経由でプリフィルした帰属コード。
  // 招待コード欄へ自動入力した値の元を控えておき、
  //  - 補足文言の表示条件
  //  - 送信時に invitation_code として送らない判定（後述）
  // に使う。プリフィルしていない場合は null。
  const [partnerRefPrefill, setPartnerRefPrefill] = useState<string | null>(
    null,
  );
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [generalError, setGeneralError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // handleSubmit で組織作成に成功すると dispatch(setUser) により
  // user.organization_id が立ち、下の所属済みガード useEffect が再発火する。
  // カード導線有効時は handleSubmit が /plans へ遷移させたい一方、この再発火が
  // /home へ replace 上書きしてしまうレースがあるため、送信を開始したら
  // ガードを黙らせる（送信フローの遷移を優先する）。
  const submittedRef = useRef(false);

  // 招待URL経由なら、SignupPage が localStorage に退避した partner_ref を
  // 招待コード欄へ自動入力し、折りたたみも開いておく。
  // 「コードを入力すべきか」とユーザーが迷わないようにするのが目的。
  // partner_ref そのものはこの欄の入力に依存せず handleSubmit で別送するため、
  // ここでのプリフィルは表示上の補助であって帰属の成否には影響しない。
  useEffect(() => {
    const stored = localStorage.getItem(PARTNER_REF_STORAGE_KEY);
    if (stored && PARTNER_REF_REGEX.test(stored)) {
      setInvitationCode(stored);
      setShowInvitationCode(true);
      setPartnerRefPrefill(stored);
    }
  }, []);

  // 既に組織へ所属済みのユーザーがこの画面へ到達したら、オンボーディング
  // 完了状態に応じた次の画面へ送る。
  // /signup/setup にルートガードが無く、所属済みユーザーが再訪して company
  // onboarding を再実行すると同名の空組織が量産される不具合があったため
  // (_docs/thinking/2026-06-01_org-duplicate-on-signup.md)。
  // user が null の過渡状態 (リロード直後でプロフィール復元前) では遷移を保留する。
  useEffect(() => {
    // 送信フロー実行中はガードを無効化する。handleSubmit 内の
    // dispatch(setUser) で organization_id が立った直後にここが発火すると、
    // 送信フローの遷移先（/plans 等）を /home へ上書きしてしまうため。
    if (submittedRef.current) return;
    if (user && user.organization_id) {
      navigate(
        !SIGNUP_ONBOARDING_ENABLED || user.onboarding_completed === true
          ? '/home'
          : '/signup/onboarding',
        { replace: true },
      );
    }
  }, [user, navigate]);

  const canSubmit =
    companyName.trim().length > 0 &&
    displayName.trim().length > 0 &&
    !isSubmitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setFieldErrors({});
    setGeneralError('');
    setIsSubmitting(true);

    const payload: {
      company_name: string;
      name: string;
      invitation_code?: string;
      partner_ref?: string;
      trial_days?: number;
      invite_ref?: string;
    } = {
      company_name: companyName.trim(),
      name: displayName.trim(),
    };
    const trimmedCode = invitationCode.trim();
    // パートナー招待URLからプリフィルした値のまま送信する場合は、
    // invitation_code として送らない。invitation_code は「既存組織の紹介コード
    // → InvitationCreditService でクレジット付与」の照合に使われるため、
    // パートナー帰属コード（partner_ref として別送）が万一いずれかの組織の
    // 紹介コードと偶然一致すると、誤ってその組織へクレジットが付与されてしまう。
    // ユーザーが値を書き換えた場合は本人が持つ紹介コードとみなし従来通り送る。
    if (trimmedCode && trimmedCode !== partnerRefPrefill) {
      payload.invitation_code = trimmedCode;
    }

    // 招待URL経由の帰属コード（SignupPage で localStorage に退避済み）を同送する。
    const partnerRef = localStorage.getItem(PARTNER_REF_STORAGE_KEY);
    if (partnerRef && PARTNER_REF_REGEX.test(partnerRef)) {
      payload.partner_ref = partnerRef;
      // 延長トライアル招待URL経由なら希望日数も同送する。実際に延長するかは
      // サーバーがポータルのパートナー設定（許可トライアル期間）で判定する。
      const trialDays = parsePartnerTrialDays(
        localStorage.getItem(PARTNER_TRIAL_DAYS_STORAGE_KEY),
      );
      if (trialDays) {
        payload.trial_days = trialDays;
      }
    }

    // 友だち紹介URL経由の帰属コード（SignupPage で localStorage に退避済み）。
    // partner_ref とは別系統なので、両方付いていればそれぞれ独立に効く。
    // 有効性（存在・未失効・上限）の判定はすべてサーバー側で行う。
    const inviteRef = localStorage.getItem(INVITE_REF_STORAGE_KEY);
    if (inviteRef && INVITE_REF_REGEX.test(inviteRef)) {
      payload.invite_ref = inviteRef;
    }

    try {
      const response = await completeOnboarding(payload);
      // 帰属コードは組織作成に成功した時点で使い切る（多重帰属を防ぐ）。
      localStorage.removeItem(PARTNER_REF_STORAGE_KEY);
      localStorage.removeItem(PARTNER_TRIAL_DAYS_STORAGE_KEY);
      localStorage.removeItem(INVITE_REF_STORAGE_KEY);
      // organization_id を Redux / localStorage に即時反映する。
      // これをしないと /signup/onboarding のガードが組織なしの古い user を見て
      // /signup/setup に弾き返してしまう（プロフィール再取得はリロード時のみ）。
      const mergedUser = { ...(user ?? {}), ...response.user } as User;
      tokenManager.saveUserData(mergedUser);
      // dispatch より前に立てる。dispatch(setUser) で organization_id が立つと
      // 所属済みガード useEffect が発火するので、その前にガードを黙らせて
      // ここでの遷移先が /home へ上書きされないようにする。
      submittedRef.current = true;
      dispatch(setUser(mergedUser));

      if (SIGNUP_ONBOARDING_ENABLED) {
        navigate('/signup/onboarding');
      } else if (SIGNUP_CARD_STEP_ENABLED) {
        // フルオンボーディング非表示中でも、プラン選択→カード登録の導線だけは
        // 通す。onboarding_completed を先に立てておかないと、将来フル
        // オンボーディングを復活させたとき、この期間に登録したユーザーが
        // /signup/onboarding へ再誘導されてしまうため、遷移前に完了扱いにする。
        // ただしこの完了マークが失敗しても導線は止めない（カード登録優先）。
        try {
          await userService.completeOnboarding();
        } catch (markErr) {
          console.warn(
            'Failed to mark onboarding completed before card step:',
            markErr,
          );
        }
        navigate('/plans?from=signup');
      } else {
        navigate('/home');
      }
    } catch (err: any) {
      console.error('Company onboarding failed:', err);

      if (err?.response?.status === 422 && err?.response?.data?.errors) {
        const errors = err.response.data.errors;
        const mapped: FieldErrors = {};
        if (errors.company_name) mapped.company_name = errors.company_name[0];
        if (errors.name) mapped.name = errors.name[0];
        if (errors.invitation_code)
          mapped.invitation_code = errors.invitation_code[0];
        setFieldErrors(mapped);
      } else if (err?.response?.data?.message) {
        setGeneralError(err.response.data.message);
      } else {
        setGeneralError(
          '情報の保存に失敗しました。しばらく時間をおいて再度お試しください。',
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <TaskyAuthLayout
      logo={
        <Image category="common" fileName="logo.png" alt="Tasky" width={100} />
      }
      title="あと少しで完了です"
      description="ご利用にあたり、会社情報を入力してください。"
    >
      {generalError && (
        <TaskyAlert tone="error" className="mb-4">
          {generalError}
        </TaskyAlert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <TaskyFormGroup
          label="会社名"
          required
          htmlFor="company-name"
          error={fieldErrors.company_name}
        >
          <TaskyInput
            id="company-name"
            type="text"
            placeholder="株式会社○○"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            hasError={Boolean(fieldErrors.company_name)}
          />
        </TaskyFormGroup>

        <TaskyFormGroup
          label="お名前"
          required
          htmlFor="display-name"
          error={fieldErrors.name}
        >
          <TaskyInput
            id="display-name"
            type="text"
            placeholder="山田 太郎"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            hasError={Boolean(fieldErrors.name)}
          />
        </TaskyFormGroup>

        <div>
          <button
            type="button"
            onClick={() => setShowInvitationCode((v) => !v)}
            className="text-sm text-primary hover:text-primary-hover underline-offset-2 hover:underline"
          >
            招待コードをお持ちの方
          </button>
          {showInvitationCode && (
            <div className="mt-3">
              <TaskyFormGroup
                label="招待コードを入力"
                htmlFor="invitation-code"
                error={fieldErrors.invitation_code}
                helper={
                  partnerRefPrefill && invitationCode === partnerRefPrefill
                    ? 'パートナー招待URLのコードを自動入力しています。このままお進みください。'
                    : undefined
                }
              >
                <TaskyInput
                  id="invitation-code"
                  type="text"
                  placeholder="お持ちの方のみご入力ください"
                  value={invitationCode}
                  onChange={(e) => setInvitationCode(e.target.value)}
                  hasError={Boolean(fieldErrors.invitation_code)}
                />
              </TaskyFormGroup>
            </div>
          )}
        </div>

        <TaskyButton
          type="submit"
          isLoading={isSubmitting}
          disabled={!canSubmit}
          className="w-full"
        >
          {isSubmitting ? '保存中...' : '次へ進む'}
        </TaskyButton>
      </form>
    </TaskyAuthLayout>
  );
};

export default CompanyOnboardingPage;
